// import { EventHook } from '@vueuse/core';
import { tryOnScopeDispose } from '@vueuse/core';

// https://stackoverflow.com/questions/55541275/typescript-check-for-the-any-type
export type IfAny<T, Y, N> = 0 extends 1 & T ? Y : N;
/**
 * will return `true` if `T` is `any`, or `false` otherwise
 */
export type IsAny<T> = IfAny<T, true, false>;
// any extends void = true
// so we need to check if T is any first
type Callback<T> = IsAny<T> extends true
  ? (...param: any) => void
  : [T] extends [void]
  ? (...param: unknown[]) => void
  : (...param: [T, ...unknown[]]) => void;

export type EventHookOn<T = any> = (fn: Callback<T>) => { off: () => void };
export type EventHookOff<T = any> = (fn: Callback<T>) => void;
export type EventHookTrigger<T = any> = (
  ...param: IsAny<T> extends true ? unknown[] : [T, ...unknown[]]
) => Promise<unknown[]>;

export interface EventHook<T = any> {
  on: EventHookOn<T>;
  off: EventHookOff<T>;
  trigger: EventHookTrigger<T>;
  clear: () => void;
}

/**
 * Source code taken from https://github.com/vueuse/vueuse/blob/main/packages/shared/createEventHook/index.ts
 *
 * Modified to be able to check if there are any event listeners
 */
export interface EventHookExtended<T = any> extends EventHook<T> {
  hasListeners: () => boolean;
  fns: Set<(param: T) => void>;
}

export function createExtendedEventHook<T = any>(
  defaultHandler?: (param: T) => void
): EventHookExtended<T> {
  const fns = new Set<(param: T) => void>();

  let hasDefaultHandler = false;

  const hasListeners = () => fns.size > 0;

  if (defaultHandler) {
    hasDefaultHandler = true;
    fns.add(defaultHandler);
  }

  const off = (fn: (param: T) => void) => {
    fns.delete(fn);
  };

  const clear = () => {
    fns.clear();
  };

  const on = (fn: (param: T) => void) => {
    if (defaultHandler && hasDefaultHandler) {
      fns.delete(defaultHandler);
    }

    fns.add(fn);

    const offFn = () => {
      off(fn);

      if (defaultHandler && hasDefaultHandler) {
        fns.add(defaultHandler);
      }
    };

    tryOnScopeDispose(offFn);

    return {
      off: offFn
    };
  };

  const trigger: EventHookTrigger<T> = (...args) => {
    return Promise.all(Array.from(fns).map(fn => fn(...(args as [T]))));
  };

  return {
    on,
    off,
    trigger,
    clear,
    hasListeners,
    fns
  };
}
